import React from 'react';

import type { ConfirmDialogProps } from '@lumapps/lumx-dialogs/components/ConfirmDialog';
import { useNotification } from '@lumapps/notifications/hooks/useNotifications';
import { GLOBAL, useTranslate } from '@lumapps/translations';

import { deleteContent as deleteContentAPI } from '../api/contents';
import { CONTENTS } from '../keys';

export const useContentDelete = ({
    openConfirmDialog,
    contentId,
    onSuccess,
}: {
    contentId?: string;
    openConfirmDialog: (props: Omit<ConfirmDialogProps, 'onConfirm' | 'onClose'>) => Promise<boolean>;
    onSuccess?: () => void;
}) => {
    const { success, error } = useNotification();
    const { translateKey } = useTranslate();

    const deleteContent = React.useCallback(async () => {
        if (!contentId) {
            return;
        }

        try {
            await deleteContentAPI(contentId);

            success({ content: translateKey(CONTENTS.DELETE_SUCCESS) });
            onSuccess?.();
        } catch (err) {
            error({ content: translateKey(GLOBAL.GENERIC_ERROR) });
        }
    }, [contentId, error, onSuccess, success, translateKey]);

    const onDelete = React.useCallback(async () => {
        if (!contentId) {
            return;
        }

        const confirmed = await openConfirmDialog({
            title: translateKey(CONTENTS.DELETE_CONFIRM_TITLE),
            confirmMessage: translateKey(CONTENTS.DELETE_CONFIRM_CONTENT),
            confirmProps: { label: translateKey(GLOBAL.DELETE) },
            id: 'delete',
        });

        if (confirmed) {
            deleteContent();
        }
    }, [contentId, openConfirmDialog, translateKey, deleteContent]);

    return {
        onDelete,
    };
};
